import React, { Component } from "react";
import messages from "./TimelineEventForm.messages";
import {
  Grid,
  Row,
  Col,
  Button,
  FormGroup,
  FormControl,
  ControlLabel,
  Modal,
} from "react-bootstrap";
import DatePicker from "react-datepicker";
import moment from "moment";
import uniq from "lodash/uniq";
import compact from "lodash/compact";
import "react-datepicker/dist/react-datepicker.css";
import "../forms.css";
import GradientButton from "../../input/GradientButton";

import type { CreateProjectParamsObject } from "../../../redux/projects/types";
import Project from "../../../models/Project";
import User from "../../../models/User";
import TimeInput from "../TimeInput";

type Props = {
  defaultTimelineEvents: TimelineEventCollection,
  projectId: string,
  projectDate: string,
  user: User,
  createTimelineEvent: (params: CreateTimelineEventObject) => Promise<void>,
  onDidCreateTimelineEvent: (projectId: string) => void,
  onRequestBack: () => void,
  onModalClose: () => void,
};

type State = {
  name: string,
  dateTime: string,
  date: string,
  dateMoment: Object,
  location: string,
  details: string,
  isCustom: boolean,
};

const DATE_FORMAT = "MM-DD-YYYY";

export default class TimelineEventForm extends Component {
  props: Props;

  state: State = {
    name: "",
    dateTime: moment.utc(formatIncomingDate(this.props.projectDate)).format(),
    date: moment
      .utc(formatIncomingDate(this.props.projectDate))
      .format(DATE_FORMAT),
    dateMoment: moment.utc(formatIncomingDate(this.props.projectDate)),
    location: "",
    details: "",
    isCustom: false,
  };

  dateRef: TextInput;
  timeRef: TextInput;
  detailsRef: TextInput;
  locationRef: TextInput;
  createEventPromise: ?Promise<void>;

  constructor(props: Props) {
    super(props);
  }

  handleNameChange = (event) => {
    this.setState({
      name: event.target.value,
      nameLength: event.target.value.length,
    });
  };

  handleShowCustomName = (event) => {
    event.preventDefault();
    this.setState({ isCustom: true });
  };

  handleLocationChange = (event) => {
    this.setState({ location: event.target.value });
  };

  handleTimeChange(dateTime) {
    if (dateTime) {
      this.setState({
        dateTime: moment.utc(dateTime).format(),
        date: moment.utc(dateTime).format(DATE_FORMAT),
        dateMoment: moment.utc(dateTime),
      });
    }
  }

  handleDateChange(date) {
    if (date) {
      this.setState({
        dateTime: moment.utc(date).format(),
        date: moment.utc(date).format(DATE_FORMAT),
        dateMoment: moment.utc(date),
      });
    }
  }

  handleNotesChange = (event) => {
    this.setState({ details: event.target.value });
  };

  handleCreateEvent = (event) => {
    event.preventDefault();
    this.createEventPromise = Promise.resolve(this.createEvent());
  };

  async createEvent() {
    if (!this.state.date) {
      return;
    }
    const event = await this.props.createTimelineEvent({
      name: this.state.name,
      dateTime: this.timezonelessDateTime(),
      location: this.state.location,
      details: this.state.details,
      projectId: this.props.projectId,
    });
    this.props.onDidCreateTimelineEvent();
  }

  timezonelessDateTime = () => {
    return moment.utc(this.state.dateTime).utcOffset(0, true).format();
  };

  getEventNames() {
    const eventNames = this.props.defaultTimelineEvents
      .toArray()
      .map((e) => e.name)
      .sort();
    const uniqueEventNames = uniq(eventNames);
    const compactEventNames = compact(uniqueEventNames);
    return compactEventNames;
  }

  render() {
    return (
      <div>
        <form onSubmit={this.handleCreateEvent}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <ControlLabel>{messages.defaultName}</ControlLabel>
                  <FormControl
                    componentClass="select"
                    name={messages.defaultName}
                    placeholder={messages.defaultName}
                    onChange={this.handleNameChange}
                  >
                    {this.getEventNames().map((value) => (
                      <option value={value} key={value}>
                        {value}
                      </option>
                    ))}
                  </FormControl>
                  <a href="" onClick={this.handleShowCustomName}>
                    {messages.addCustomName}
                  </a>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <ControlLabel>{messages.location}</ControlLabel>
                  <FormControl
                    type="text"
                    name={messages.location}
                    placeholder={messages.location}
                    onChange={this.handleLocationChange}
                  />
                </FormGroup>
              </Col>
            </Row>
            {this.state.isCustom ? this.renderCustomName() : ""}
            <Row>
              <Col md={6}>
                <FormGroup>
                  <ControlLabel>{messages.date}</ControlLabel>
                  <DatePicker
                    className="form-control"
                    id="dateTimePicker"
                    onChange={this.handleDateChange.bind(this)}
                    showTimeSelect
                    timeIntervals={15}
                    selected={this.state.dateMoment}
                    name={messages.date}
                    placeholder={messages.date}
                    timeCaption="Time"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <ControlLabel>{messages.time}</ControlLabel>
                  <br></br>
                  <TimeInput
                    message={messages.time}
                    time={this.state.dateTime}
                    onRequestChangeTime={this.handleTimeChange.bind(this)}
                  ></TimeInput>
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <FormGroup>
                  <ControlLabel>{messages.notes}</ControlLabel>
                  <FormControl
                    componentClass="textarea"
                    name={messages.notes}
                    placeholder={messages.notes}
                    onChange={this.handleNotesChange}
                  />
                </FormGroup>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <GradientButton
              gradient="0/1"
              handleClick={this.props.onModalClose}
              title="Cancel"
            />
            <button type="submit" style={{ padding: 0, border: "none" }}>
              <GradientButton
                gradient="1/1"
                handleClick={this.handleAddEvent}
                title="Add Timeline Item"
              />
            </button>
          </Modal.Footer>
        </form>
      </div>
    );
  }

  renderCustomName() {
    const maxNameLength = 60;

    return (
      <Row>
        <Col md={6}>
          <FormGroup>
            <ControlLabel>
              {messages.customName}
              {` ${~~this.state.nameLength}/${maxNameLength}`}
            </ControlLabel>
            <FormControl
              type="text"
              name={messages.customName}
              placeholder={messages.customName}
              onChange={this.handleNameChange}
              maxLength={maxNameLength}
            />
          </FormGroup>
        </Col>
      </Row>
    );
  }
}

const formatIncomingDate = (inDate) => {
  const [month, day, year] = inDate.split("/");
  return `${year}-${month}-${day}T00:00:00Z`;
};



// WEBPACK FOOTER //
// ./src/components/forms/timeline-event-form/TimelineEventForm.js